import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import BottomSheetModal from '../../../components/BottomSheetModal';
import type { IScreenNudge } from '../../../interfaces/Withdrawal';
import styles from '../styles.module.css';

interface ScreenNudgeSheetProps {
  isOpen: boolean;
  nudge?: IScreenNudge;
  onAgree: () => void;
  onDismiss: () => void;
}

/**
 * A screen nudge returned alongside the withdrawal gate.
 *
 * Renders the generic shape only. Android also has radio-option, timer and
 * rewards formats with their own layouts; those arrive here as a plain nudge
 * rather than being drawn incorrectly.
 */
const ScreenNudgeSheet: React.FC<ScreenNudgeSheetProps> = ({
  isOpen,
  nudge,
  onAgree,
  onDismiss,
}) => {
  const { t } = useTranslation();
  if (!nudge) return null;

  const content = (
    <div className={styles.nudgeBody}>
      {nudge.nudgeSubTitle && (
        <p className={clsx(styles.nudgeText, 'fontSizeSm14')}>
          {nudge.nudgeSubTitle}
        </p>
      )}
      {nudge.nudgeDescription && (
        <p className={clsx(styles.nudgeText, 'fontSizeSm12 fontColor666')}>
          {nudge.nudgeDescription}
        </p>
      )}
    </div>
  );

  const actions = [
    {
      label: nudge.agreeButtonText || t('continue'),
      onClick: onAgree,
      btnClass: 'primaryBtn',
    },
    ...(nudge.avoidButtonText
      ? [
          {
            label: nudge.avoidButtonText,
            onClick: onDismiss,
            btnClass: 'secondaryBtn',
          },
        ]
      : []),
  ];

  return (
    <BottomSheetModal
      isOpen={isOpen}
      modalIcon={nudge.nudgeIcon}
      title={nudge.nudgeTitle}
      content={content}
      actions={actions}
      // A hard nudge must be acted on rather than dismissed.
      onClose={nudge.isSoftNudge === false ? () => {} : onDismiss}
    />
  );
};

export default ScreenNudgeSheet;
